import { useState, useEffect, useCallback } from 'react'

export interface UserLocation {
  lat: number
  lng: number
}

export interface GeolocationState {
  location: UserLocation | null
  error: string | null
  isLoading: boolean
  requestLocation: () => void
}

function getGeolocationErrorMessage(error: GeolocationPositionError): string {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return 'Location permission was denied. Enable location access in your browser settings and try again.'
    case error.POSITION_UNAVAILABLE:
      return 'Your location is not available. Check your device location settings and try again.'
    case error.TIMEOUT:
      return 'The location request timed out. Try again in an open area.'
    default:
      return 'Your location could not be found. Try again.'
  }
}

export function useGeolocation(autoRequest: boolean = false): GeolocationState {
  const [state, setState] = useState<GeolocationState>({
    location: null,
    error: null,
    isLoading: autoRequest,
    requestLocation: () => {},
  })

  const requestLocation = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setState(prev => ({
        ...prev,
        error: 'This browser does not support location access.',
        isLoading: false,
      }))
      return
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }))

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setState(prev => ({
          ...prev,
          location: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          },
          error: null,
          isLoading: false,
        }))
      },
      (err) => {
        setState(prev => ({
          ...prev,
          location: null,
          error: getGeolocationErrorMessage(err),
          isLoading: false,
        }))
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 60000,
      }
    )
  }, [])

  useEffect(() => {
    setState(prev => ({ ...prev, requestLocation }))
  }, [requestLocation])

  useEffect(() => {
    if (autoRequest) {
      requestLocation()
    }
  }, [autoRequest, requestLocation])

  return state
}
